const SESSION_TOKEN_KEY = "DS";
const REFRESH_TOKEN_KEY = "DSR";

export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    const encoded = parts[1];
    if (parts.length !== 3 || !encoded) return null;
    const payload = atob(encoded.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;
  return Date.now() >= payload.exp * 1000;
}

export function getTokenClaims(token: string): {
  sub?: string;
  email?: string;
  name?: string;
  exp?: number;
} | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  return {
    sub: payload.sub as string | undefined,
    email: payload.email as string | undefined,
    name: payload.name as string | undefined,
    exp: payload.exp as number | undefined,
  };
}

interface RefreshResult {
  ok: boolean;
  sessionJwt?: string;
}

let refreshPromise: Promise<RefreshResult> | null = null;

export async function refreshSession(
  projectId: string,
): Promise<RefreshResult> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = doRefresh(projectId).finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

async function doRefresh(projectId: string): Promise<RefreshResult> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return { ok: false };

  try {
    const response = await fetch(
      "https://api.descope.com/v1/auth/try-refresh?dcs=t&dcr=t",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${projectId}:${refreshToken}`,
          "x-descope-project-id": projectId,
        },
        body: "{}",
      },
    );

    if (!response.ok) return { ok: false };

    const data = (await response.json()) as {
      sessionJwt?: string;
      refreshJwt?: string;
    };

    if (data.sessionJwt) {
      localStorage.setItem(SESSION_TOKEN_KEY, data.sessionJwt);
    }
    if (data.refreshJwt) {
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshJwt);
    }

    return { ok: true, sessionJwt: data.sessionJwt };
  } catch {
    return { ok: false };
  }
}

export interface DescopeUserInfo {
  userId: string;
  email?: string;
  name?: string;
  picture?: string;
}

/**
 * Fetch user info from Descope's /v1/auth/me endpoint.
 * This replaces the Descope SDK's useUser() hook with a
 * lightweight HTTP call that doesn't require loading the SDK.
 */
export async function fetchUserInfo(
  projectId: string,
): Promise<DescopeUserInfo | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const response = await fetch("https://api.descope.com/v1/auth/me", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${projectId}:${refreshToken}`,
      },
    });

    if (!response.ok) return null;

    const data = (await response.json()) as {
      userId?: string;
      email?: string;
      name?: string;
      picture?: string;
    };

    return {
      userId: data.userId ?? "",
      email: data.email,
      name: data.name,
      picture: data.picture,
    };
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export async function logoutSession(projectId: string): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await fetch("https://api.descope.com/v1/auth/logout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${projectId}:${refreshToken}`,
        },
        body: "{}",
      });
    } catch {
      // Best effort - clear local tokens regardless
    }
  }
  clearTokens();
}

/**
 * Notify the SessionAuthProvider that tokens changed in the same tab.
 * The `storage` event only fires in other tabs, so this custom event
 * handles same-tab updates (e.g. after login/signup Descope flows).
 */
export function notifySessionChange(): void {
  window.dispatchEvent(new CustomEvent("headerhawk:session-change"));
}
